import { ArrowUpRight, ArrowDownRight } from "lucide-react";
import { MONTH_NAMES, formatMoney, isNegative, sumDecimals, roundCents } from "@/lib/format";

const Num = ({ v, tone, testId }) => (
  <td className={`px-4 py-2.5 text-right font-mono ${tone || (isNegative(v) ? "text-rose-300" : "")}`} data-testid={testId}>{formatMoney(v)}</td>
);

const neg = (v) => (String(v).startsWith("-") ? String(v).slice(1) : `-${v}`);
const hasData = (m) => roundCents(m.income) !== "0.00" || roundCents(m.expenditure) !== "0.00";

// Better or worse than the same month(s) last year.
const Change = ({ now, before, show, testId, strong }) => {
  if (!show) return <td className="px-4 py-2.5 text-right text-muted-foreground" data-testid={testId}>—</td>;
  const diff = sumDecimals([now, neg(before)]);
  const down = isNegative(diff);
  const same = roundCents(diff) === "0.00";
  const Icon = down ? ArrowDownRight : ArrowUpRight;
  return (
    <td className={`px-4 py-2.5 text-right ${strong ? "font-semibold" : ""}`} data-testid={testId}>
      <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 font-mono text-xs ${same ? "text-muted-foreground" : down ? "bg-rose-500/10 text-rose-500" : "bg-emerald-500/10 text-emerald-500"}`}>
        {!same && <Icon className="h-3.5 w-3.5" />}{down ? "" : "+"}{formatMoney(diff)}
        <span className="font-sans">{same ? "same" : down ? "worse" : "better"}</span>
      </span>
    </td>
  );
};

export default function MonthlyTable({ months, totals, prevFy }) {
  const done = months.filter(hasData);
  const ytdNow = sumDecimals(done.map((m) => m.net));
  const ytdBefore = sumDecimals(done.map((m) => m.prev_net));
  const upTo = done.length ? MONTH_NAMES[done[done.length - 1].month - 1] : "";
  return (
    <div className="border border-border bg-card overflow-x-auto">
      <table className="w-full text-sm" data-testid="cashflow-monthly-table">
        <thead>
          <tr className="text-right text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
            <th className="px-4 py-3 text-left font-medium">Month</th>
            <th className="px-4 py-3 font-medium">Income</th><th className="px-4 py-3 font-medium">Expenditure</th>
            <th className="px-4 py-3 font-medium">Net cash</th><th className="px-4 py-3 font-medium">Forecast</th>
            <th className="px-4 py-3 font-medium">Net {prevFy}</th>
            <th className="px-4 py-3 font-medium">vs same month last year</th>
          </tr>
        </thead>
        <tbody>
          {months.map((m) => (
            <tr key={m.month} className="border-b border-border hover:bg-accent/30 transition-colors" data-testid={`cashflow-month-${m.month}`}>
              <td className="px-4 py-2.5 font-mono text-xs"><span className="text-muted-foreground mr-2">{m.month}</span>{MONTH_NAMES[m.month - 1]}</td>
              <Num v={m.income} tone="text-emerald-300" testId={`cashflow-income-${m.month}`} />
              <Num v={m.expenditure} tone="text-rose-300" testId={`cashflow-expenditure-${m.month}`} />
              <Num v={m.net} testId={`cashflow-net-${m.month}`} />
              <Num v={m.forecast} tone="text-amber-300" testId={`cashflow-forecast-${m.month}`} />
              <Num v={m.prev_net} tone="text-indigo-300" testId={`cashflow-prev-net-${m.month}`} />
              <Change now={m.net} before={m.prev_net} show={hasData(m)} testId={`cashflow-vs-last-year-${m.month}`} />
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="font-semibold">
            <td className="px-4 py-3">FY total</td>
            <Num v={totals.income} tone="text-emerald-300" testId="cashflow-total-income" />
            <Num v={totals.expenditure} tone="text-rose-300" testId="cashflow-total-expenditure" />
            <Num v={totals.net} testId="cashflow-total-net" />
            <Num v={totals.forecast} tone="text-amber-300" testId="cashflow-total-forecast" />
            <Num v={sumDecimals(months.map((m) => m.prev_net))} tone="text-indigo-300" testId="cashflow-total-prev-net" />
            <td />
          </tr>
          {done.length > 0 && (
            <tr className="border-t border-border bg-accent/30" data-testid="cashflow-same-time-last-year">
              <td className="px-4 py-3 font-semibold" colSpan={3}>Same time last year <span className="font-normal text-muted-foreground">(Jul to {upTo})</span></td>
              <Num v={ytdNow} testId="cashflow-ytd-net" />
              <td />
              <Num v={ytdBefore} tone="text-indigo-300" testId="cashflow-ytd-prev-net" />
              <Change now={ytdNow} before={ytdBefore} show strong testId="cashflow-ytd-change" />
            </tr>
          )}
        </tfoot>
      </table>
    </div>
  );
}
